import type {
  ArgumentType, Band, ChallengeCategory, ClaimType, ComparisonDimension,
  EpistemicStatus, EvidenceStrength, EvidenceType, Locale, Necessity,
  RelationKind, Significance, SourceLayer, Weight, WeightFactor,
} from '../primitives/enums.js';

/**
 * §16 nível 1 — rótulos que nunca podem aparecer como chave crua na UI.
 * Vivem no schema porque são dados canónicos, não estilo: a app, o backoffice
 * e a IA usam os mesmos. I17 verifica que estão completos nos três locales.
 */
export type LabelTable<K extends string> = Record<Locale, Record<K, string>>;

export const EPISTEMIC_STATUS_LABELS: LabelTable<EpistemicStatus> = {
  en: {
    established: 'Established', strongly_supported: 'Strongly Supported',
    probable: 'Probable', plausible: 'Plausible', disputed: 'Disputed',
    highly_disputed: 'Highly Disputed', unknown: 'Unknown',
    philosophical_theological: 'Philosophical / Theological',
  },
  es: {
    established: 'Establecido', strongly_supported: 'Sólidamente respaldado',
    probable: 'Probable', plausible: 'Plausible', disputed: 'En disputa',
    highly_disputed: 'Muy disputado', unknown: 'Desconocido',
    philosophical_theological: 'Filosófico / teológico',
  },
  'pt-PT': {
    established: 'Estabelecido', strongly_supported: 'Fortemente sustentado',
    probable: 'Provável', plausible: 'Plausível', disputed: 'Disputado',
    highly_disputed: 'Muito disputado', unknown: 'Desconhecido',
    philosophical_theological: 'Filosófico / teológico',
  },
};

export const EVIDENCE_STRENGTH_LABELS: LabelTable<EvidenceStrength> = {
  en: { very_strong: 'Very strong', strong: 'Strong', moderate: 'Moderate', weak: 'Weak', disputed: 'Disputed' },
  es: { very_strong: 'Muy sólida', strong: 'Sólida', moderate: 'Moderada', weak: 'Débil', disputed: 'En disputa' },
  'pt-PT': { very_strong: 'Muito forte', strong: 'Forte', moderate: 'Moderada', weak: 'Fraca', disputed: 'Disputada' },
};

export const SIGNIFICANCE_LABELS: LabelTable<Significance> = {
  en: { critical: 'Critical', major: 'Major', supporting: 'Supporting', contextual: 'Contextual' },
  es: { critical: 'Crítica', major: 'Mayor', supporting: 'De apoyo', contextual: 'Contextual' },
  'pt-PT': { critical: 'Crítica', major: 'Maior', supporting: 'De apoio', contextual: 'Contextual' },
};

export const EVIDENCE_TYPE_LABELS: LabelTable<EvidenceType> = {
  en: {
    archaeology: 'Archaeology', manuscript: 'Manuscript', epigraphy: 'Epigraphy',
    numismatics: 'Numismatics', primary_literary: 'Primary literary',
    external_literary: 'External literary', historical_event: 'Historical event',
    medical: 'Medical', textual_criticism: 'Textual criticism', linguistic: 'Linguistic',
    legal_administrative: 'Legal / administrative', material_culture: 'Material culture',
  },
  es: {
    archaeology: 'Arqueología', manuscript: 'Manuscrito', epigraphy: 'Epigrafía',
    numismatics: 'Numismática', primary_literary: 'Fuente literaria primaria',
    external_literary: 'Fuente literaria externa', historical_event: 'Hecho histórico',
    medical: 'Médica', textual_criticism: 'Crítica textual', linguistic: 'Lingüística',
    legal_administrative: 'Legal / administrativa', material_culture: 'Cultura material',
  },
  'pt-PT': {
    archaeology: 'Arqueologia', manuscript: 'Manuscrito', epigraphy: 'Epigrafia',
    numismatics: 'Numismática', primary_literary: 'Fonte literária primária',
    external_literary: 'Fonte literária externa', historical_event: 'Acontecimento histórico',
    medical: 'Médica', textual_criticism: 'Crítica textual', linguistic: 'Linguística',
    legal_administrative: 'Legal / administrativa', material_culture: 'Cultura material',
  },
};

export const SOURCE_LAYER_LABELS: LabelTable<SourceLayer> = {
  en: {
    hebrew_bible: 'Hebrew Bible', second_temple_text: 'Second Temple text',
    pauline_letter: 'Pauline letter', synoptic_gospel: 'Synoptic gospel',
    johannine: 'Johannine', other_nt: 'Other New Testament',
    apostolic_father: 'Apostolic Father', later_church_tradition: 'Later church tradition',
    external_greco_roman: 'External Greco-Roman source',
    quran: 'Quran', early_manuscript: 'Early manuscript', hadith: 'Hadith', sira: 'Sira',
    tafsir: 'Tafsir', fiqh: 'Fiqh', early_non_muslim_source: 'Early non-Muslim source',
    later_islamic_tradition: 'Later Islamic tradition',
    material_record: 'Material record', modern_scholarship: 'Modern scholarship',
  },
  es: {
    hebrew_bible: 'Biblia hebrea', second_temple_text: 'Texto del Segundo Templo',
    pauline_letter: 'Carta paulina', synoptic_gospel: 'Evangelio sinóptico',
    johannine: 'Joánico', other_nt: 'Otro del Nuevo Testamento',
    apostolic_father: 'Padre apostólico', later_church_tradition: 'Tradición eclesial posterior',
    external_greco_roman: 'Fuente grecorromana externa',
    quran: 'Corán', early_manuscript: 'Manuscrito temprano', hadith: 'Hadiz', sira: 'Sira',
    tafsir: 'Tafsir', fiqh: 'Fiqh', early_non_muslim_source: 'Fuente no musulmana temprana',
    later_islamic_tradition: 'Tradición islámica posterior',
    material_record: 'Registro material', modern_scholarship: 'Investigación moderna',
  },
  'pt-PT': {
    hebrew_bible: 'Bíblia Hebraica', second_temple_text: 'Texto do Segundo Templo',
    pauline_letter: 'Carta paulina', synoptic_gospel: 'Evangelho sinóptico',
    johannine: 'Joanino', other_nt: 'Outro do Novo Testamento',
    apostolic_father: 'Padre Apostólico', later_church_tradition: 'Tradição eclesial posterior',
    external_greco_roman: 'Fonte greco-romana externa',
    quran: 'Alcorão', early_manuscript: 'Manuscrito antigo', hadith: 'Hadith', sira: 'Sira',
    tafsir: 'Tafsir', fiqh: 'Fiqh', early_non_muslim_source: 'Fonte não-muçulmana antiga',
    later_islamic_tradition: 'Tradição islâmica posterior',
    material_record: 'Registo material', modern_scholarship: 'Investigação moderna',
  },
};

export const CHALLENGE_CATEGORY_LABELS: LabelTable<ChallengeCategory> = {
  en: {
    apparent_contradiction: 'Apparent contradiction', ethical_challenge: 'Ethical challenge',
    scientific_cosmological: 'Scientific / cosmological claim', historical_claim: 'Historical claim',
    prophecy: 'Prophecy', miracle_claim: 'Miracle claim', difficult_saying: 'Difficult saying',
    legal_moral_command: 'Legal / moral command', violence: 'Violence', slavery: 'Slavery',
    sexual_ethics_marriage: 'Sexual ethics / marriage', divine_identity: 'Divine identity',
    eschatology: 'Eschatology',
  },
  es: {
    apparent_contradiction: 'Contradicción aparente', ethical_challenge: 'Cuestión ética',
    scientific_cosmological: 'Afirmación científica o cosmológica', historical_claim: 'Afirmación histórica',
    prophecy: 'Profecía', miracle_claim: 'Afirmación de milagro', difficult_saying: 'Dicho difícil',
    legal_moral_command: 'Mandato legal o moral', violence: 'Violencia', slavery: 'Esclavitud',
    sexual_ethics_marriage: 'Ética sexual y matrimonio', divine_identity: 'Identidad divina',
    eschatology: 'Escatología',
  },
  'pt-PT': {
    apparent_contradiction: 'Contradição aparente', ethical_challenge: 'Questão ética',
    scientific_cosmological: 'Afirmação científica ou cosmológica', historical_claim: 'Afirmação histórica',
    prophecy: 'Profecia', miracle_claim: 'Afirmação de milagre', difficult_saying: 'Dito difícil',
    legal_moral_command: 'Mandamento legal ou moral', violence: 'Violência', slavery: 'Escravatura',
    sexual_ethics_marriage: 'Ética sexual e casamento', divine_identity: 'Identidade divina',
    eschatology: 'Escatologia',
  },
};

export const CLAIM_TYPE_LABELS: LabelTable<ClaimType> = {
  en: {
    historical: 'Historical', textual: 'Textual', archaeological: 'Archaeological',
    theological: 'Theological', supernatural: 'Supernatural', philosophical: 'Philosophical',
  },
  es: {
    historical: 'Histórica', textual: 'Textual', archaeological: 'Arqueológica',
    theological: 'Teológica', supernatural: 'Sobrenatural', philosophical: 'Filosófica',
  },
  'pt-PT': {
    historical: 'Histórica', textual: 'Textual', archaeological: 'Arqueológica',
    theological: 'Teológica', supernatural: 'Sobrenatural', philosophical: 'Filosófica',
  },
};

/**
 * §4 — "necessário" é uma condição para a tradição, não um grau de prova.
 * Confundir os dois é o erro que a etiqueta tem de evitar, e é por isso que
 * o rótulo diz para quê e não quanto.
 */
export const NECESSITY_LABELS: LabelTable<Necessity> = {
  en: {
    necessary: 'Necessary for the tradition',
    supporting: 'Supporting',
    peripheral: 'Peripheral',
  },
  es: {
    necessary: 'Necesaria para la tradición',
    supporting: 'De apoyo',
    peripheral: 'Periférica',
  },
  'pt-PT': {
    necessary: 'Necessária para a tradição',
    supporting: 'De apoio',
    peripheral: 'Periférica',
  },
};

export const ARGUMENT_TYPE_LABELS: LabelTable<ArgumentType> = {
  en: {
    historical: 'Historical', archaeological: 'Archaeological', manuscript: 'Manuscript',
    textual: 'Textual', linguistic: 'Linguistic', scientific: 'Scientific',
    philosophical: 'Philosophical', theological: 'Theological', moral_ethical: 'Moral / ethical',
    internal_consistency: 'Internal consistency', external_corroboration: 'External corroboration',
  },
  es: {
    historical: 'Histórico', archaeological: 'Arqueológico', manuscript: 'Manuscrito',
    textual: 'Textual', linguistic: 'Lingüístico', scientific: 'Científico',
    philosophical: 'Filosófico', theological: 'Teológico', moral_ethical: 'Moral / ético',
    internal_consistency: 'Coherencia interna', external_corroboration: 'Corroboración externa',
  },
  'pt-PT': {
    historical: 'Histórico', archaeological: 'Arqueológico', manuscript: 'Manuscrito',
    textual: 'Textual', linguistic: 'Linguístico', scientific: 'Científico',
    philosophical: 'Filosófico', theological: 'Teológico', moral_ethical: 'Moral / ético',
    internal_consistency: 'Coerência interna', external_corroboration: 'Corroboração externa',
  },
};

/**
 * §109 — o rótulo da aresta é lido junto ao desenho, por isso é uma frase
 * curta e não um termo técnico: "requer" e "depende fortemente de" dizem a
 * diferença que a linha cheia e a tracejada só sugerem.
 */
export const RELATION_KIND_LABELS: LabelTable<RelationKind> = {
  en: {
    supports: 'supports', challenges: 'challenges', contextualizes: 'contextualizes',
    requires: 'requires', strongly_depends_on: 'strongly depends on',
    independent_of: 'independent of', conflicts_with: 'conflicts with',
    objects_to: 'objects to', responds_to: 'responds to', counters: 'counters',
    agrees_with: 'agrees with', in_tension_with: 'in tension with', parallels: 'parallels',
    quotes: 'quotes', alludes_to: 'alludes to',
    transmits_to: 'transmits to', attested_by: 'attested by',
    critical_edition_of: 'critical edition of', translation_of: 'translation of',
    literary_dependency: 'literary dependency', common_earlier_source: 'common earlier source',
    same_tradition: 'same tradition',
    belongs_to_topic: 'belongs to topic', cites: 'cites', illustrates: 'illustrates',
  },
  es: {
    supports: 'apoya', challenges: 'cuestiona', contextualizes: 'contextualiza',
    requires: 'requiere', strongly_depends_on: 'depende fuertemente de',
    independent_of: 'independiente de', conflicts_with: 'entra en conflicto con',
    objects_to: 'objeta a', responds_to: 'responde a', counters: 'contesta',
    agrees_with: 'concuerda con', in_tension_with: 'en tensión con', parallels: 'es paralelo a',
    quotes: 'cita', alludes_to: 'alude a',
    transmits_to: 'transmite a', attested_by: 'atestiguado por',
    critical_edition_of: 'edición crítica de', translation_of: 'traducción de',
    literary_dependency: 'dependencia literaria', common_earlier_source: 'fuente anterior común',
    same_tradition: 'misma tradición',
    belongs_to_topic: 'pertenece al tema', cites: 'cita', illustrates: 'ilustra',
  },
  'pt-PT': {
    supports: 'apoia', challenges: 'contesta', contextualizes: 'contextualiza',
    requires: 'requer', strongly_depends_on: 'depende fortemente de',
    independent_of: 'independente de', conflicts_with: 'está em conflito com',
    objects_to: 'objecta a', responds_to: 'responde a', counters: 'contra-argumenta',
    agrees_with: 'concorda com', in_tension_with: 'em tensão com', parallels: 'é paralelo a',
    quotes: 'cita', alludes_to: 'alude a',
    transmits_to: 'transmite a', attested_by: 'atestado por',
    critical_edition_of: 'edição crítica de', translation_of: 'tradução de',
    literary_dependency: 'dependência literária', common_earlier_source: 'fonte anterior comum',
    same_tradition: 'mesma tradição',
    belongs_to_topic: 'pertence ao tópico', cites: 'cita', illustrates: 'ilustra',
  },
};

/**
 * §119 — o vocabulário do motor de robustez.
 *
 * É deliberadamente **separado** de `EpistemicStatus`, apesar de partilhar
 * palavras: o estado é um juízo editorial sobre a afirmação; a banda é o que
 * resta dela depois de se retirarem pressupostos. Juntá-los faria parecer que
 * o motor reescreve o estado, que é a coisa que ele nunca faz.
 */
export const BAND_LABELS: LabelTable<Band> = {
  en: {
    very_strong: 'Very strong', strong: 'Strong', moderate: 'Moderate', weak: 'Weak',
    disputed: 'Disputed', removed: 'Removed by the configuration',
    not_applicable: 'Nothing here to test',
  },
  es: {
    very_strong: 'Muy sólido', strong: 'Sólido', moderate: 'Moderado', weak: 'Débil',
    disputed: 'En disputa', removed: 'Retirado por la configuración',
    not_applicable: 'Nada que poner a prueba',
  },
  'pt-PT': {
    very_strong: 'Muito forte', strong: 'Forte', moderate: 'Moderado', weak: 'Fraco',
    disputed: 'Disputado', removed: 'Retirado pela configuração',
    not_applicable: 'Nada aqui para testar',
  },
};

export const COMPARISON_DIMENSION_LABELS: LabelTable<ComparisonDimension> = {
  en: {
    historical_evidence: 'Historical evidence', textual_evidence: 'Textual evidence',
    archaeological_context: 'Archaeological context', scientific_evidence: 'Scientific evidence',
    philosophical_argument: 'Philosophical argument', theological_coherence: 'Theological coherence',
    external_corroboration: 'External corroboration', internal_consistency: 'Internal consistency',
  },
  es: {
    historical_evidence: 'Evidencia histórica', textual_evidence: 'Evidencia textual',
    archaeological_context: 'Contexto arqueológico', scientific_evidence: 'Evidencia científica',
    philosophical_argument: 'Argumento filosófico', theological_coherence: 'Coherencia teológica',
    external_corroboration: 'Corroboración externa', internal_consistency: 'Coherencia interna',
  },
  'pt-PT': {
    historical_evidence: 'Evidência histórica', textual_evidence: 'Evidência textual',
    archaeological_context: 'Contexto arqueológico', scientific_evidence: 'Evidência científica',
    philosophical_argument: 'Argumento filosófico', theological_coherence: 'Coerência teológica',
    external_corroboration: 'Corroboração externa', internal_consistency: 'Coerência interna',
  },
};

export const WEIGHT_LABELS: LabelTable<Weight> = {
  en: { very_low: 'Very low', low: 'Low', medium: 'Medium', high: 'High', very_high: 'Very high' },
  es: { very_low: 'Muy bajo', low: 'Bajo', medium: 'Medio', high: 'Alto', very_high: 'Muy alto' },
  'pt-PT': { very_low: 'Muito baixo', low: 'Baixo', medium: 'Médio', high: 'Alto', very_high: 'Muito alto' },
};

export const WEIGHT_FACTOR_LABELS: LabelTable<WeightFactor> = {
  en: {
    temporal_proximity: 'Temporal proximity', source_independence: 'Source independence',
    manuscript_evidence: 'Manuscript evidence', academic_consensus: 'Academic consensus',
    archaeology: 'Archaeology', internal_theology: 'Internal theology',
    philosophical_coherence: 'Philosophical coherence',
  },
  es: {
    temporal_proximity: 'Proximidad temporal', source_independence: 'Independencia de fuentes',
    manuscript_evidence: 'Evidencia manuscrita', academic_consensus: 'Consenso académico',
    archaeology: 'Arqueología', internal_theology: 'Teología interna',
    philosophical_coherence: 'Coherencia filosófica',
  },
  'pt-PT': {
    temporal_proximity: 'Proximidade temporal', source_independence: 'Independência de fontes',
    manuscript_evidence: 'Evidência manuscrita', academic_consensus: 'Consenso académico',
    archaeology: 'Arqueologia', internal_theology: 'Teologia interna',
    philosophical_coherence: 'Coerência filosófica',
  },
};

/** Todas as tabelas de nível 1, para I17. */
export const TIER1_LABEL_TABLES = {
  epistemic_status: EPISTEMIC_STATUS_LABELS,
  evidence_strength: EVIDENCE_STRENGTH_LABELS,
  significance: SIGNIFICANCE_LABELS,
  evidence_type: EVIDENCE_TYPE_LABELS,
  source_layer: SOURCE_LAYER_LABELS,
  challenge_category: CHALLENGE_CATEGORY_LABELS,
  claim_type: CLAIM_TYPE_LABELS,
  necessity: NECESSITY_LABELS,
  argument_type: ARGUMENT_TYPE_LABELS,
  relation_kind: RELATION_KIND_LABELS,
  band: BAND_LABELS,
  comparison_dimension: COMPARISON_DIMENSION_LABELS,
  weight: WEIGHT_LABELS,
  weight_factor: WEIGHT_FACTOR_LABELS,
} as const;

// ── formatação por locale (§16 §3) ───────────────────────────

/**
 * Separadores de milhar por locale.
 *
 * `Intl.NumberFormat` faz isto e está em todo o lado, mas devolve resultados
 * que variam com a versão do ICU do runtime — e um número que muda de forma
 * entre o servidor e o browser é a fonte de bugs de hidratação mais difícil de
 * ver. Estas três línguas têm regras simples e fixas, por isso ficam escritas.
 */
const GROUPING: Record<Locale, { group: string; decimal: string }> = {
  en: { group: ',', decimal: '.' },
  es: { group: '.', decimal: ',' },
  'pt-PT': { group: ' ', decimal: ',' },
};

export function formatNumber(value: number, locale: Locale = 'en', decimals = 0): string {
  const { group, decimal } = GROUPING[locale];
  const fixed = Math.abs(value).toFixed(decimals);
  const [whole = '0', fraction] = fixed.split('.');
  // Abaixo de cinco dígitos não se agrupa: "7957 unidades" lê-se melhor do
  // que "7 957", e é a convenção tipográfica das três línguas.
  const grouped = whole.length > 4
    ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, group)
    : whole;
  const sign = value < 0 ? '-' : '';
  return fraction === undefined ? `${sign}${grouped}` : `${sign}${grouped}${decimal}${fraction}`;
}

/** Percentagem com o espaço que cada língua usa antes do símbolo. */
export function formatPercent(value: number, locale: Locale = 'en', decimals = 0): string {
  const n = formatNumber(value, locale, decimals);
  return locale === 'en' ? `${n}%` : `${n} %`;
}
